package com.oasisfeng.invaluable.p2p.core

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjectionScheme
import com.oasisfeng.invaluable.identity.Codec
import kotlinx.io.bytestring.ByteString

/**
 * The control plane of replicated-event sync: the lines two peers exchange to agree on *what data to send*, as opposed
 * to the data itself (which is [com.oasisfeng.invaluable.event.BatchWireCodec]'s batch envelope).
 *
 * Three lines, all base64url with the scheme implicit — one scheme per store, per the
 * [com.oasisfeng.invaluable.event.LineWireCodec] convention:
 *
 * ```
 * HEADS|<credential>=<head>,<head>|<credential>=<head>|…   the sender's per-actor commitment frontier
 * SYNCED                                                    every batch owed for the preceding frontier has been sent
 * WANT|<projection>                                         re-fetch one node by content address (KTD7)
 * ```
 *
 * **One definition, because there are two speakers.** [ReplicatedEventSync] and
 * the retired Link-aware relay were separate implementations by design — that relay archived whole
 * batches and never applies them — but they are not separate *protocols*, and they used to spell this one out twice,
 * each with its own tag constants and its own decoder. The copies had already drifted: one accepted a `HEADS` line
 * whose tag it never checked (so `HEADSanything` read as an empty frontier and drew the entire history), the other
 * rejected it; one accepted `WANT|x|y`, the other did not. Neither divergence was reachable from either module's tests,
 * because each side's tests wrote the wire out by hand rather than feeding one implementation's encoder to the other's
 * decoder. A wire form that two places spell out is a wire form that can disagree with itself, and the failure mode is
 * silent: peers stay connected, exchange lines, and never converge.
 *
 * This is also a **release-compatibility surface**. The relay is built and deployed from its own environment, so a
 * deployed relay and a running client are always two independently released speakers of these three lines; changing one
 * of them is a protocol break, not a refactor.
 */
object SyncControlWire {

    const val FRONTIER_TAG = "HEADS"
    const val SYNCED_TAG = "SYNCED"
    const val WANT_TAG = "WANT"

    fun isFrontier(line: String): Boolean = line.hasTag(FRONTIER_TAG)

    fun isWant(line: String): Boolean = line.hasTag(WANT_TAG)

    fun encodeFrontier(frontier: Map<LinkProjection, Set<LinkProjection>>): String = buildList {
        add(FRONTIER_TAG)
        for ((credential, heads) in frontier) {
            add(encode(credential) + ASSIGN + heads.joinToString(LIST_SEPARATOR, transform = ::encode))
        }
    }.joinToString(FIELD_SEPARATOR)

    /** The advertised frontier, or null if [line] is not a well-formed [FRONTIER_TAG] line. An empty map is a peer that holds nothing. */
    fun decodeFrontier(line: String, scheme: LinkProjectionScheme): Map<LinkProjection, Set<LinkProjection>>? =
        runCatching {
            val fields = line.trim().split(FIELD_SEPARATOR)
            if (fields.first() != FRONTIER_TAG) return null
            fields.drop(1).filter(String::isNotEmpty).associate { entry ->
                val parts = entry.split(ASSIGN, limit = 2)
                require(parts.size == 2)
                decode(parts[0], scheme) to parts[1].split(LIST_SEPARATOR)
                    .filter(String::isNotEmpty).map { decode(it, scheme) }.toSet()
            }
        }.getOrNull()

    fun encodeWant(projection: LinkProjection): String = WANT_TAG + FIELD_SEPARATOR + encode(projection)

    /** The requested projection, or null if [line] is not a well-formed [WANT_TAG] line. */
    fun decodeWant(line: String, scheme: LinkProjectionScheme): LinkProjection? = runCatching {
        val fields = line.trim().split(FIELD_SEPARATOR)
        if (fields.size != 2 || fields[0] != WANT_TAG || fields[1].isEmpty()) return null
        decode(fields[1], scheme)
    }.getOrNull()

    private fun String.hasTag(tag: String): Boolean = this == tag || startsWith(tag + FIELD_SEPARATOR)
    private fun encode(projection: LinkProjection): String = Codec.base64Url(projection.payload.toByteArray())
    private fun decode(field: String, scheme: LinkProjectionScheme): LinkProjection =
        LinkProjection(scheme, ByteString(*Codec.base64UrlDecode(field)))

    private const val FIELD_SEPARATOR = "|"
    private const val LIST_SEPARATOR = ","
    private const val ASSIGN = "="
}
